import DeleteIconButton from '../components/DeleteIconButton';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Autocomplete, Box, Button, Divider, FormControl, FormControlLabel, Grid, IconButton, InputLabel, Link, Select, Switch, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import QRCode from 'qrcode.react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory, useLocation, useParams } from 'react-router';

import { DataAPIContext, DELETE, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import BlockRedirect from '../components/BlockRedirect';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import ImageGalleryEditor from '../components/ImageGalleryEditor';
import LicenseeFormField from '../components/LicenseeFormField';
import NavigateBackBar from '../components/NavigateBackBar';
import PageMeta from '../components/PageMeta';
import QuillEditor from '../components/QuillEditor';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import { a11yProps, TabPanel } from '../components/TabPanel';
import TripflowEditor from '../components/TripflowEditor';
import UsedByPanel from '../components/UsedByPanel';
import UploadImageButton from '../components/UploadImageButton';
import { ContextStore } from '../store/contextStore';
import { checkAndAddHttpPrefix, EMDASH, fromJSONPath } from '../utils';
import { useIsWidthUp } from '../utils';
import { copyTextToClipboard } from '../utils/clipboard';
import validURL from '../utils/validators/url';
import { appSupportContacts, ConfirmationDialogText, CTA_BEHAVIOR_LINK_INAPP, CTA_BEHAVIOR_LINK_SYSTEM, CTA_BEHAVIOR_PHONE_NUMBER, CTA_BEHAVIOR_ADD_TRIP,
    CTA_BEHAVIOR_EXPLORE, CTA_BEHAVIOR_EXPLORE_OR_ADD_TRIP,
    ctaBehaviorsMenuItems, ctaTripDeparturesMenuItems, displayCurrenciesMenuItems, MONTH } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        height: theme.cp.contentHeight,
        margin: theme.spacing(2),
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 5rem)`,
            padding: theme.spacing(0),
            margin: theme.spacing(0),
            marginBottom: theme.spacing(4),
            overflowY: 'auto',
        },
    },

    buttonPanel: {
        backgroundColor: grey[100],
        zIndex: 1700,
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        [theme.breakpoints.down('sm')]: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
        },
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: 2,
    },
    textFieldLink: {
        // color: theme.palette.primary.main,
        // cursor: 'pointer',
    },
}));

const getComparableState = (trip) => {
    const a = { ...trip };
    // remove everything that is synced separately
    delete a.images;
    return JSON.stringify(a);
};

function DownloadQRCode(props) {
    // download QR code
    const downloadQRCode = () => {
        const qrCodeURL = document.getElementById('qrCodeEl').toDataURL('image/png').replace('image/png', 'image/octet-stream');
        let aEl = document.createElement('a');
        aEl.href = qrCodeURL;
        aEl.download = 'QR_Code.png';
        document.body.appendChild(aEl);
        aEl.click();
        document.body.removeChild(aEl);
    };

    return (
        <>
            <QRCode id="qrCodeEl" size={200} value={props.value} />
            <br />
            <Button color="primary" variant="outlined" onClick={downloadQRCode}>
                Download
            </Button>
        </>
    );
}

const validateInput = (itinerary) => {
    const errors = {};
    let canSave = true;
    if (!itinerary.name || itinerary.name.length <= 0 || itinerary.name === '') {
        if (itinerary._changed) {
            errors.name = { error: true, helperText: 'Itinerary name is required' };
        }
        canSave = false;
    }

    return { canSave, errors };
};

const ItineraryEditForm = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const urlParams = useParams();
    const location = useLocation();
    const paramsToTab = {
        details: 0,
        images: 1,
        itinerary: 2,
        changes: 3,
        usedby: 4,
    };

    const { currencyMap, tourOperator, currentLicensee } = React.useContext(ContextStore);
    const { hasPerm } = React.useContext(ContextStore);
    const [errors, setErrors] = React.useState({});
    const isSmallScreen = !useIsWidthUp('sm');
    const currentTab = paramsToTab[urlParams.tab] ? paramsToTab[urlParams.tab] : 0;
    const [itinerary, setItinerary] = React.useState(() => {
        const data = props.data;
        if (!data.id && !data.tour_operator && currentLicensee) {
            return { ...data, tour_operator: currentLicensee };
        }
        return data;
    });
    const [originalState, setOriginalState] = React.useState(getComparableState(itinerary));
    const [syncActive, setSyncActive] = React.useState(false);
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const [clientErrors, setClientErrors] = React.useState({
        missingBookingURL: false,
        missingCTAButtonBehavior: false,
        missingSupportContactsButtonBehavior: false,
        disabledSupportContactsButtonBehavior: false,
    });

    const currentState = getComparableState(itinerary);
    const hasChanged = originalState !== currentState;
    const [bookingUrlErrorState, setBookingUrlErrorState] = React.useState(false);
    const [, setEditImageState] = React.useState(false);

    const hasWritePerm = hasPerm('itinerary', 'write')

    const onLicenseeChange = (id) => {
        setItinerary((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const { canSave } = validateInput(itinerary);

    React.useEffect(() => {
        if (paramsToTab[urlParams.tab] === void 0) {
            history.replace(`${location.pathname}${location.pathname.endsWith('/') ? '' : '/'}details`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTab, urlParams.id, urlParams.tab]);



    React.useEffect(() => {
        if (urlParams.tab === 'images' && urlParams.imageid && isSmallScreen) {
            setEditImageState(true);
        } else {
            setEditImageState(false);
        }
    }, [urlParams]);

    React.useEffect(() => {
        setClientErrors((prev) => ({ ...prev, missingSupportContactsButtonBehavior: false, disabledSupportContactsButtonBehavior: false }));

        const supportContacts = appSupportContacts(tourOperator.support_contacts);
        if (supportContacts.length === 0 && itinerary.cta_button_behavior === 2) {
            setClientErrors((prev) => ({ ...prev, missingSupportContactsButtonBehavior: true }));
        }
        if (supportContacts.length > 0 && supportContacts.filter((sc) => sc.enabled).length === 0 && itinerary.cta_button_behavior === 2) {
            setClientErrors((prev) => ({ ...prev, disabledSupportContactsButtonBehavior: true }));
        }
    }, [itinerary.cta_button_behavior]);



    const handleTabChange = (ev, newTab) => {
        const match = Object.keys(paramsToTab).filter((x) => paramsToTab[x] === newTab);
        if (match && match.length === 1) {
            const newURL = `/explore/itineraries/${urlParams.id}/${match[0]}`;
            history.push(newURL);
        }
    };


    const onDropRouteMap = React.useCallback(
        (acceptedFiles) => {
            setItinerary({ ...itinerary, route_map: acceptedFiles[0], upload: true });
        },
        [itinerary]
    );

    const clearRouteMap = (ev) => {
        setItinerary({ ...itinerary, route_map: null });
    };

    const routeMapDZ = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDropRouteMap,
        multiple: false,
    });

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
            dir: theme.direction,
            buttonBoxSpacing: true,
            style: {
                padding: theme.spacing(3),
                maxHeight: !isSmallScreen && 'calc(100svh - 19.7rem)',
                overflowY: !isSmallScreen && 'auto',
            },
        };
    };

    const prepareSaveData = (data) => {
        const flist = {};
        let hasUpload = data && data.route_map && data.route_map.constructor === File;
        const res = {
            ...data,
            days: (data.days || []).map((day, idx) => {
                if (day.image && day.image.constructor === File) {
                    flist[day.sequence] = day.image;
                }
                return day;
            }),
        };
        Object.keys(flist).forEach((dayseq) => {
            res[`day_image_${dayseq}`] = flist[dayseq];
            hasUpload = true;
        });
        return { preparedData: res, upload: hasUpload };
    };

    const handleSave = (ev) => {
        if ((itinerary.cta_button_behavior === 0 || itinerary.cta_button_behavior === 1) && !itinerary.booking_url) {
            setClientErrors((prev) => ({ ...prev, missingBookingURL: true }));
            return;
        }

        if (itinerary.cta_button_text && (itinerary.cta_button_behavior === undefined || itinerary.cta_button_behavior === null)) {
            setClientErrors((prev) => ({ ...prev, missingCTAButtonBehavior: true }));
            return;
        }
        setSyncActive(true);
        itinerary.booking_url = checkAndAddHttpPrefix(itinerary.booking_url);

        const { preparedData, upload } = prepareSaveData(itinerary);

        const params = {
            id: itinerary.id,
            data: preparedData,
            upload: upload,
        };

        dataAPI(UPDATE, props.resource, params)
            .then((res) => {
                if (res.statusCode === 500) {
                    enqueueSnackbar('Error saving itinerary, our servers are currently down due to maintenance, please try again in a few minutes or contact support if the issue persist', { variant: 'error' });
                    return;
                }
                if (res.statusCode === 400 || res.statusCode === 404) {
                    enqueueSnackbar('Error saving itinerary, this is probably due to validation or authorization, please contact support if the issue persist', { variant: 'error' });
                    return;
                }
                enqueueSnackbar('Itinerary saved', { variant: 'success' });
                setItinerary(res.data);
                setOriginalState(getComparableState(res.data));
            })
            .catch((err) => {
                enqueueSnackbar('Error saving itinerary', { variant: 'error' });
            })
            .finally(() => {
                setSyncActive(false);
            });
    };

    const doDelete = (ev) => {
        console.log('deleting', props.data.id);
        props.onDelete && props.onDelete(props.data.id);
        setSyncActive(true);
        (async () => {
            try {
                const res = await dataAPI(DELETE, props.resource, { id: props.data.id });
                enqueueSnackbar('Itinerary deleted', { variant: 'default' });
                console.log('delete', res);
                history.push(`/explore/itineraries`, { deleted: props.data.id });
            } catch (e) {
                console.log('error deleting itinerary', e);
                enqueueSnackbar('Error deleting itinerary', { variant: 'error' });
            } finally {
                setSyncActive(false);
            }
        })();
    };

    const onTextChange = (ev) => {
        if (ev.target.name === 'booking_url') {
            setClientErrors((prev) => ({ ...prev, missingBookingURL: false }));
        }
        setItinerary((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, _changed: true }));
    };

    const onSwitchChange = (ev) => {
        setItinerary((prevState) => ({ ...prevState, [ev.target.name]: ev.target.checked }));
    };

    const onSelectChange = (ev, nv) => {
        if (ev.target.name === 'cta_button_behavior') {
            if (ev.target.value === 2) {
                setClientErrors((prev) => ({ ...prev, missingCTAButtonBehavior: false, missingBookingURL: false }));
                setItinerary((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, booking_url: '' }));
                return;
            } else {
                setClientErrors((prev) => ({ ...prev, missingCTAButtonBehavior: false }));
            }
        }
        setItinerary((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value }));
    };

    const onCurrencyChange = (ev, newValue) => {
        setItinerary((prevState) => ({ ...prevState, currency: +newValue }));
    };

    const onRegionChange = (ev, nv) => {
        setItinerary((prevState) => ({ ...prevState, regions: nv }));
    };

    const onFilterChange = (ev, nv) => {
        setItinerary((prevState) => ({ ...prevState, trip_types: nv }));
    };

    const onItineraryTagChange = (ev, nv) => {
        setItinerary((prevState) => ({ ...prevState, tags: nv }));
    };

    const onImagesChange = (gallery) => {
        setItinerary((prevState) => ({ ...prevState, images: gallery }));
    };

    const onTripflowChange = (update, upload) => {
        setItinerary(fromJSONPath(update));
    };

    const onTripflowInsert = (day, idx) => {
        setItinerary((prevState) => {
            const days = prevState.days || [];
            const newDays = Array.from(Array(days.length + 1).keys()).map((n) => {
                if (n < idx) {
                    return { ...days[n], sequence: n + 1 };
                } else if (n === idx) {
                    return { ...day, sequence: n + 1 };
                } else {
                    return { ...days[n - 1], sequence: n + 1 };
                }
            });
            return {
                ...prevState,
                days: newDays,
            };
        });
    };

    const onTripflowDelete = (idx) => {
        setItinerary((prevState) => ({
            ...prevState,
            days: (prevState.days || []).filter((d, i) => i !== idx),
        }));
    };

    const handleMonthChange = (ev, newValue) => {
        setItinerary((prevState) => ({ ...prevState, months: newValue.map((item) => item.id) }));
    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setItinerary((prevState) => ({ ...prevState, atag: atags }));
    };

    const isExistingRouteImage = itinerary.route_map && itinerary.route_map.p && itinerary.route_map.s;

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_itinerary':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };


    const handleBookingURLOnBlur = () => {
        if (validURL(itinerary.booking_url)) {
            setBookingUrlErrorState(false);
        } else {
            setBookingUrlErrorState(true);
        }
    };

    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    const handleOnBlur = (ev) => {
        setErrors(validateInput(itinerary).errors);
    };


    if (!hasPerm('itinerary', 'read')) {
        return <AccessDeniedView />;
    }

    const itineraryListUrl = props.lastURLParams?.size > 0 ? '/explore/itineraries?' + props.lastURLParams?.toString() : '/explore/itineraries';

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(itinerary.tour_operator)}>
        <div className={classes.root}>
            <BlockRedirect shouldBlock={hasChanged} message={'Itinerary: are you sure you want to leave without saving changes?'} ignoredPaths={['/explore/itineraries']} />
            <PageMeta title={`Edit Itinerary - ${itinerary.name}`} />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}

            <FixedLinearProgress visibility={syncActive} />
            <NavigateBackBar label={itinerary.name} url={itineraryListUrl} maxLabelLength={isSmallScreen ? 40 : 120} />
            <Divider />
            <Grid container>
                <Grid item xs={12}>
                    <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable">
                        <Tab label="General info" {...a11yProps(0)} />
                        <Tab label="Images" {...a11yProps(1)} />
                        <Tab label="ITINERARY HIGHLIGHTS" {...a11yProps(2)} />
                        {hasPerm('itinerary', 'view_history') && <Tab label="Change history" {...a11yProps(3)} />}
                        {itinerary.id && <Tab label="Used by" {...a11yProps(4)} />}
                    </Tabs>
                </Grid>
                <Grid item xs={12}>
                    <Divider />
                    <TabPanel {...tabPanelProps(0)}>
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12} sm={8} container direction="row" spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h6">General</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <LicenseeFormField value={itinerary.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel disabled={!hasWritePerm} control={<Switch checked={itinerary.published} name="published" color="primary" onChange={onSwitchChange} />} label="Publish" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        disabled={!hasWritePerm}
                                        label="Name"
                                        variant="filled"
                                        size="small"
                                        name="name"
                                        required
                                        fullWidth
                                        value={itinerary.name || ''}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            maxLength: 256,
                                        }}
                                        helperText={`${256 - (itinerary.name || '').length} characters left`}
                                        onChange={onTextChange}
                                        onBlur={handleOnBlur}
                                        {...errors.name}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        disabled={!hasWritePerm}
                                        label="Duration Display"
                                        variant="filled"
                                        size="small"
                                        name="duration_display"
                                        fullWidth
                                        value={itinerary.duration_display || ''}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            maxLength: 40,
                                        }}
                                        onChange={onTextChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        disabled={!hasWritePerm}
                                        label="2-Line Synopsis"
                                        variant="filled"
                                        size="small"
                                        name="description"
                                        multiline
                                        fullWidth
                                        value={itinerary.description || ''}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            rowsMax: 2,
                                        }}
                                        onChange={onTextChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography style={{ color: '#737373', fontSize: '90%', marginBottom: '-0.15rem' }}>Brief Overview (Header)</Typography>
                                    <Box display="flex" flexGrow={1} style={{ width: isSmallScreen ? '85vw' : '100%', padding: '0.2rem' }}>
                                        {!hasWritePerm ?
                                            <TextField disabled={true} fullWidth variant="filled" size="small" multiline value={itinerary.highlights} onChange={onTextChange} />
                                            :
                                        <QuillEditor 
                                            containerHeight='250px'
                                            value={itinerary.highlights}
                                            onChange={(content) => onTextChange({ target: { name: 'highlights', value: content } })}
                                            />}
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={Object.keys(MONTH)
                                            .map((key) => ({
                                                id: parseInt(key),
                                                name: MONTH[key],
                                            }))
                                            .filter((option) => !itinerary.months.includes(option.id))}
                                        getOptionLabel={(option) => option.name}
                                        value={Object.keys(MONTH)
                                            .map((key) => ({
                                                id: parseInt(key),
                                                name: MONTH[key],
                                            })).filter((month) => itinerary.months.includes(month.id))}
                                        onChange={(event, newValue) => handleMonthChange(event, newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                label="Operating Months"
                                            />
                                        )}
                                        disabled={!hasWritePerm}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        disabled={!hasWritePerm}
                                        label="Deep Link"
                                        type="url"
                                        size="small"
                                        name="branchio_link"
                                        value={itinerary.branchio_link || EMDASH}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        variant="filled"
                                        InputProps={{
                                            className: classes.textFieldLink,
                                            endAdornment: (
                                                <IconButton
                                                    aria-label="copy deep link to clipboard"
                                                    onClick={(ev) => {
                                                        copyToClipboard(itinerary.branchio_link, 'Deep link');
                                                    }}
                                                    edge="end"
                                                >
                                                    <FileCopyIcon />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box pb={2} pt={0}>
                                        {itinerary.branchio_link && <DownloadQRCode value={itinerary.branchio_link} />}
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6">Route Map</Typography>
                                </Grid>
                                {/* CP-5556: [UPLOAD IMAGE] / [REPLACE IMAGE] on the image's bottom-right corner and the trash on its
                                    top-right, like Trip Info and Edit Bus. */}
                                <Grid item xs={12}>
                                    <input {...routeMapDZ.getInputProps()} />
                                    <div style={{ position: 'relative', width: 300 }}>
                                        <UploadImageButton
                                            disabled={!hasWritePerm}
                                            borderRadius={5}
                                            width={300}
                                            imageAction="overlay"
                                            onClick={routeMapDZ.open}
                                            isUpload={!isExistingRouteImage}
                                            image={itinerary.route_map}
                                        />
                                        {itinerary.route_map ? (
                                            <span style={{ position: 'absolute', top: 11, right: 11 }}>
                                                <DeleteIconButton title="Delete image" overImage allowed={hasWritePerm} onClick={clearRouteMap} />
                                            </span>
                                        ) : null}
                                    </div>
                                </Grid>
                                <Grid item xs={12} pr={1}>
                                    <Typography style={{ color: '#737373', fontSize: '90%', marginBottom: '-0.15rem' }}>Footer (Optional)</Typography>
                                    <Box display="flex" flexGrow={1} style={{ width: isSmallScreen ? '85vw' : '100%', padding: '0.2rem' }}>
                                        {!hasWritePerm ?
                                            <TextField disabled={true} fullWidth variant="filled" size="small" multiline value={itinerary.footer} onChange={onTextChange} />
                                            :
                                            <QuillEditor
                                            containerHeight='250px'
                                            value={itinerary.footer}
                                            onChange={(content) => onTextChange({ target: { name: 'footer', value: content } })}
                                            />}

                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">Pricing</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField disabled={!hasWritePerm} label="Cost Prefix" variant="filled" size="small" name="cost_prefix" value={itinerary.cost_prefix || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={onTextChange} helperText="e.g., From" />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField disabled={!hasWritePerm} label="Cost" type="number" variant="filled" size="small" name="cost" value={itinerary.cost || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={onTextChange} />
                                </Grid>
                                <Grid item xs={4}>
                                    <Autocomplete
                                        disabled={!hasWritePerm}
                                        value={itinerary.currency}
                                        onChange={onCurrencyChange}
                                        noOptionsText={'No matches'}
                                        options={Object.keys(currencyMap)}
                                        name="currency"
                                        getOptionSelected={(option, value) => +option === +value}
                                        getOptionLabel={(option) => (currencyMap[option] ? `${currencyMap[option].iso} - ${currencyMap[option].name}` : '---')}
                                        renderInput={(params) => <TextField {...params} label="Choose Currency" variant="filled" size="small" inputProps={{ ...params.inputProps, autoComplete: 'new-password', name: 'currency' }} />}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField disabled={!hasWritePerm} label="Cost Unit" variant="filled" size="small" name="cost_unit" value={itinerary.cost_unit || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={onTextChange} helperText="e.g., per person" />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl variant="filled" size="small" fullWidth disabled={!hasWritePerm}>
                                        <InputLabel>Display Currency</InputLabel>
                                        <Select value={itinerary.catalog_display_currency} inputProps={{ name: 'catalog_display_currency' }} onChange={onSelectChange}>
                                            {displayCurrenciesMenuItems}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6">Call to action button</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        disabled={!hasWritePerm}
                                        label="Button Text"
                                        variant="filled"
                                        size="small"
                                        name="cta_button_text"
                                        value={itinerary.cta_button_text || ''}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        onChange={onTextChange}
                                        helperText="If this field is empty, button will not be displayed"
                                        />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl variant="filled" size="small" fullWidth className={classes.formControl} error={clientErrors.missingCTAButtonBehavior} disabled={!hasWritePerm}>
                                        <InputLabel htmlFor="button-behavior-select-element">Button Behavior</InputLabel>
                                        <Select value={itinerary.cta_button_behavior} onChange={onSelectChange} inputProps={{ name: 'cta_button_behavior', id: 'button-behavior-select-element' }}>
                                            { ctaBehaviorsMenuItems }
                                        </Select>
                                    </FormControl>
                                    {clientErrors.missingCTAButtonBehavior && (
                                        <Typography color="secondary" fontSize={12}>
                                            Button Behavior is required when button text is provided
                                        </Typography>
                                    )}
                                    {clientErrors.missingSupportContactsButtonBehavior && (
                                        <Typography color="secondary" fontSize={12}>
                                            No Support Contact is added yet. <Link onClick={() => history.push('/settings/general')}>Add Support Contact(s)</Link>
                                        </Typography>
                                    )}
                                    {clientErrors.disabledSupportContactsButtonBehavior && !clientErrors.missingSupportContactsButtonBehavior && (
                                        <Typography color="secondary" fontSize={12}>
                                            Support Contact is currently disabled. <Link onClick={() => history.push('/settings/general')}>Enable Support Contact(s)</Link>
                                        </Typography>
                                    )}
                                </Grid>
                                { [CTA_BEHAVIOR_LINK_INAPP, CTA_BEHAVIOR_LINK_SYSTEM].indexOf(+itinerary.cta_button_behavior) >= 0 && (
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Booking URL"
                                            variant="filled"
                                            size="small"
                                            name="booking_url"
                                            value={itinerary.booking_url || ''}
                                            disabled={+itinerary.cta_button_behavior === CTA_BEHAVIOR_PHONE_NUMBER || !hasWritePerm}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            onChange={onTextChange}
                                            onBlur={handleBookingURLOnBlur}
                                            error={bookingUrlErrorState || clientErrors.missingBookingURL}
                                            helperText={bookingUrlErrorState && 'Please enter a valid URL.'}
                                        />
                                        {clientErrors.missingBookingURL && (
                                            <Typography color="secondary" fontSize={12}>
                                                Booking URL is required when Link button behavior is chosen
                                            </Typography>
                                        )}
                                    </Grid>
                                )}

                                { [CTA_BEHAVIOR_ADD_TRIP, CTA_BEHAVIOR_EXPLORE, CTA_BEHAVIOR_EXPLORE_OR_ADD_TRIP].includes(+itinerary.cta_button_behavior) && (
                                    <Grid item xs={12} sm={6}>
                                        <RemoteAutocomplete
                                            disabled={!hasWritePerm}
                                            resource="tour"
                                            apiFilters={[{ field: 'time', value: "nonbespoke"}]}
                                            label="Target tour"
                                            variant="filled"
                                            size="small"
                                            onChange={(ev, nv) => setItinerary((prev) => ({ ...prev, cta_tour: nv }))}
                                            value={itinerary.cta_tour}
                                            textFieldProps={{
                                                helperText: itinerary.cta_tour ? `Available departure times: ${itinerary.cta_tour?.departure_times?.join(', ') || 'N/A'}` : null,
                                            }}
                                            getOptionLabel={(opt) => {
                                                if (opt.is_bespoke) {
                                                    return '[id:' + opt.id + '] ' + opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                                                }
                                                return '[id:' + opt.id + '] ' + opt.name + ' (Recurring)';
                                            }}
                                            renderOption={(props, option) => {
                                                if (option.is_bespoke) {
                                                    return (
                                                        <div {...props}>
                                                            [id:{option.id}] {option.name} ({option.start_date} - {option.end_date})
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div {...props}>
                                                        [id:{option.id}] {option.name} (Recurring)
                                                    </div>
                                                );
                                            }}
                                        />
                                    </Grid>
                                )}
                                { [CTA_BEHAVIOR_ADD_TRIP, CTA_BEHAVIOR_EXPLORE_OR_ADD_TRIP].includes(+itinerary.cta_button_behavior) && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl variant="filled" size="small" fullWidth className={classes.formControl} disabled={!hasWritePerm}>
                                            <InputLabel htmlFor="cta-tour-departure-select-element">Target Tour Departure</InputLabel>
                                            <Select value={itinerary.cta_tour_departure} onChange={onSelectChange} inputProps={{ name: 'cta_tour_departure', id: 'cta-tour-departure-select-element' }}>
                                                { ctaTripDeparturesMenuItems }
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="h6">Itinerary IDs</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        disabled={true}
                                        variant="outlined"
                                        color="primary"
                                        title="Internal ID"
                                        label="Internal ID"
                                        value={itinerary.id}
                                        fullWidth
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <IconButton
                                                    aria-label="copy itinerary id to clipboard"
                                                    onClick={() => {
                                                        copyToClipboard(itinerary.id, 'Itinerary ID');
                                                    }}
                                                    edge="end"
                                                >
                                                    <FileCopyIcon />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        disabled={!hasWritePerm}
                                        variant="filled"
                                        color="primary"
                                        title="External ID"
                                        name="custom_id"
                                        label="External ID"
                                        value={itinerary.custom_id}
                                        placeholder={'Set external ID'}
                                        onChange={onTextChange}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    aria-label="copy custom itinerary id to clipboard"
                                                    onClick={() => {
                                                        copyToClipboard(itinerary.custom_id, 'Itinerary custom ID');
                                                    }}
                                                    edge="end"
                                                >
                                                    <FileCopyIcon />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Grid item xs={12}>
                                    <Typography variant="h6">Associated Regions</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mb={2}>
                                        <RemoteAutocomplete disabled={!hasWritePerm} filterSelected multiple resource="region" urlSuffix={null} label="Choose Regions" variant="filled" size="small" onChange={onRegionChange} value={itinerary.regions} />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6">Associated Filters</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mb={2}>
                                        <RemoteAutocomplete disabled={!hasWritePerm} filterSelected multiple resource="trip_type" urlSuffix={null} label="Choose Filters" variant="filled" size="small" onChange={onFilterChange} value={itinerary.trip_types} />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6">Associated Tags</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mb={2}>
                                        <RemoteAutocomplete disabled={!hasWritePerm} filterSelected multiple resource="itinerary_tag" label="Choose Tags" variant="filled" size="small" onChange={onItineraryTagChange} value={itinerary.tags} />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6">Access Tags</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box mb={6}>
                                        <AccessTagsDropdown disabled={!hasWritePerm} value={itinerary.atag} onChange={onAccessTagsChange} />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </TabPanel>
                    <TabPanel {...tabPanelProps(1)}>
                        <ImageGalleryEditor
                            hasEditPermission={hasWritePerm}
                            hasDeletePermission={hasPerm('itinerary', 'delete')}
                            list={itinerary.images}
                            resource="itinerary_images"
                            resourceMeta={{ itinerary: itinerary.itinerary_id, itinerary_id: itinerary.itinerary_id }}
                            onChange={onImagesChange}
                            navigationPrefix={`/explore/itineraries/${urlParams.id}/images`}
                            imageId={urlParams.imageid}
                            isSmallScreen={isSmallScreen}
                        />
                    </TabPanel>
                    <TabPanel {...tabPanelProps(2)}>
                        <TripflowEditor hasEditPermission={hasWritePerm} itinerary={itinerary} setItinerary={setItinerary} days={itinerary.days} onChange={onTripflowChange} onDelete={onTripflowDelete} onInsert={onTripflowInsert} />
                    </TabPanel>
                    <TabPanel value={currentTab} index={3} style={{ margin: '-1rem', marginBottom: '3rem', overflowX: 'hidden' }} className={isSmallScreen ? classes.horizontalTab : classes.verticalTab} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                        <RevisionHistoryDialog open={currentTab === 3} resource={props.resource} id={itinerary.id} />
                    </TabPanel>
                    {itinerary.id && <TabPanel {...tabPanelProps(4)}>
                        <UsedByPanel resource={props.resource} id={itinerary.id} />
                    </TabPanel>}
                </Grid>
            </Grid>
            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    mode="edit"
                    onSubmit={handleSave}
                    submitDisabled={!hasWritePerm || clientErrors.missingBookingURL || syncActive || !hasChanged || bookingUrlErrorState || !canSave}
                    onCancel={() => history.push(itineraryListUrl)}
                    cancelDisabled={syncActive}
                />
            </Box>
        </div>
        </FormLicenseeContext.Provider>
    );
};

const ItineraryEdit = (props) => {
    const routeParams = useParams();
    const id = routeParams.id;
    const resource = 'itinerary';
    const { hasPerm } = React.useContext(ContextStore);

    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }

    return (
        <RecordDetails resource={resource} id={id}>
            <RecordDetailsContext.Consumer>{(data) => <ItineraryEditForm lastURLParams={props.lastURLParams} resource={resource} id={id} data={data} />}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default ItineraryEdit;
